import React, { useState, useEffect } from 'react';
import CategoryService from '../services/category.service';
import { Category } from '../interfaces/Category';
import { User } from '../interfaces/User';
import UserService from '../services/user.service';

// Force file to be a module
export {};

interface CategoryAssignmentProps {
  currentUserRole: number;
  currentUserId: number;
  selectedCategory?: Category | null;  // Optional selected category, can be null or undefined
}

const CategoryAssignment: React.FC<CategoryAssignmentProps> = ({ 
  currentUserRole,
  currentUserId,
  selectedCategory
}) => {
  // State for categories and users
  const [categories, setCategories] = useState<Category[]>([]);
  const [adminUsers, setAdminUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserCategories, setSelectedUserCategories] = useState<number[]>([]);
  const [availableCategories, setAvailableCategories] = useState<Category[]>([]);
  
  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [savingAssignments, setSavingAssignments] = useState<boolean>(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'danger' | 'warning';
  }>({
    show: false,
    message: '',
    type: 'success',
  });
  // Fetch all categories and admin users on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        await Promise.all([fetchCategories(), fetchAdminUsers()]);
        
        // If a selectedCategory was passed, pre-select it
        if (selectedCategory) {
          const categoryIds = selectedCategory ? [selectedCategory.id] : [];
          setSelectedUserCategories(categoryIds);
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        showNotification('Failed to load data. Please try again.', 'danger');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [selectedCategory]);

  // Fetch assigned categories when user is selected
  useEffect(() => {
    if (selectedUserId) {
      fetchUserCategories(selectedUserId);
    } else {
      // Reset selections when no user is selected
      setSelectedUserCategories([]);
      setAvailableCategories(categories);
    }
  }, [selectedUserId, categories]);

  const fetchCategories = async () => {
    try {
      const response = await CategoryService.getAllCategories();
      if (response && (response.categories || response.data)) {
        const allCategories = response.categories || response.data || [];
        setCategories(allCategories);
        setAvailableCategories(allCategories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      showNotification('Failed to fetch categories', 'danger');
    }
  };
  const fetchAdminUsers = async () => {
    try {
      // Fetch users with admin roles (admin = 2, system_incharge = 3, super_admin = 5)
      const response = await UserService.getUsers('', 1, -1);
      if (response && response.users) {
        // Filter users with admin roles (2 = admin, 3 = system_incharge, 5 = super_admin)
        const admins = response.users.filter(
          (user: User) => user.role_id === 2 || user.role_id === 3 || user.role_id === 5
        );
        setAdminUsers(admins);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
      showNotification('Failed to fetch admin users', 'danger');
    }
  };

  const fetchUserCategories = async (userId: number) => {
    try {
      setLoading(true);
      const response = await CategoryService.getCategoriesForAdmin(userId);
      if (response && response.data) {
        // Extract category IDs from the user's assigned categories
        const assignedCategoryIds = response.data.map((cat: Category) => cat.id);
        setSelectedUserCategories(assignedCategoryIds);
      } else {
        setSelectedUserCategories([]);
      }
    } catch (error) {
      console.error(`Error fetching categories for user ${userId}:`, error);
      showNotification('Failed to fetch user categories', 'danger');
      setSelectedUserCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUserChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const userId = parseInt(e.target.value);
    setSelectedUserId(userId || null);
  };

  const handleCategoryToggle = (categoryId: number) => {
    setSelectedUserCategories(prevSelected => {
      if (prevSelected.includes(categoryId)) {
        return prevSelected.filter(id => id !== categoryId);
      } else {
        return [...prevSelected, categoryId];
      }
    });
  };

  const handleSaveAssignments = async () => {
    if (!selectedUserId) {
      showNotification('Please select a user first', 'warning');
      return;
    }

    setSavingAssignments(true);
    try {
      await CategoryService.assignCategoriesToAdmin(
        selectedUserId, 
        { categoryIds: selectedUserCategories }
      );
      showNotification('Categories assigned successfully', 'success');
    } catch (error: any) {
      console.error('Error saving category assignments:', error);
      showNotification(
        `Error: ${error?.message || 'Failed to save assignments'}`, 
        'danger'
      );
    } finally {
      setSavingAssignments(false);
    }
  };

  // Helper function to get role display name
  const getRoleDisplayName = (roleId: number): string => {
    switch (roleId) {
      case 1: return 'User';
      case 2: return 'Admin'; 
      case 3: return 'System Incharge';
      case 4: return 'Developer';
      case 5: return 'Super Admin';
      default: return 'Unknown Role';
    }
  };

  const showNotification = (message: string, type: 'success' | 'danger' | 'warning') => {
    setNotification({
      show: true,
      message,
      type,
    });

    // Auto-hide after 3 seconds
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 3000);
  };
  return (
    <div className="category-assignment">
      {notification.show && (
        <div className={`alert alert-${notification.type} alert-dismissible fade show`} role="alert">
          {notification.message}
          <button 
            type="button" 
            className="btn-close"
            onClick={() => setNotification({...notification, show: false})}
          ></button>
        </div>
      )}

        <div className="mb-4">
          <label htmlFor="adminUserSelect" className="form-label">
            Select Admin User
          </label>
          <select
            id="adminUserSelect"
            className="form-select"
            value={selectedUserId || ''}
            onChange={handleUserChange}
            disabled={loading || adminUsers.length === 0}
          >
            <option value="">-- Select a user --</option>            {adminUsers.map(user => (
              <option key={user.user_id} value={user.user_id}>
                {user.first_name} {user.last_name} ({getRoleDisplayName(user.role_id)})
              </option>
            ))}
          </select>
          {adminUsers.length === 0 && !loading && (
            <div className="form-text text-danger">
              No admin users found in the system
            </div>
          )}
        </div>

        {selectedUserId && (
          <>              <div className="mb-3">
                <h5>Assign Categories</h5>
                <p className="text-muted small">
                  {selectedCategory 
                    ? `Assign "${selectedCategory.title}" category to the selected admin user`
                    : 'Select which categories this admin can manage'}
                </p>
                {selectedCategory && (
                  <div className="alert alert-info">
                    <small>
                      <i className="bi bi-info-circle me-1"></i>
                      You can also select additional categories to assign
                    </small>
                  </div>
                )}
              </div>

            {loading ? (
              <div className="text-center p-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="category-list">
                {categories.length > 0 ? (
                  <div className="row">
                    {categories.map(category => (
                      <div className="col-md-6 mb-2" key={category.id}>                          <div className={`form-check ${selectedCategory?.id === category.id ? 'bg-light rounded p-2' : ''}`}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`category-${category.id}`}
                              checked={selectedUserCategories.includes(category.id)}
                              onChange={() => handleCategoryToggle(category.id)}
                            />
                            <label 
                              className={`form-check-label ${selectedCategory?.id === category.id ? 'fw-bold' : ''}`} 
                              htmlFor={`category-${category.id}`}
                            >
                              {category.title}
                              {selectedCategory?.id === category.id && (
                                <span className="badge bg-info ms-2">Selected</span>
                              )}
                            </label>
                          </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="alert alert-warning">
                    No categories available to assign
                  </div>
                )}
              </div>
            )}

            <div className="mt-4">
              <button
                className="btn btn-primary"
                onClick={handleSaveAssignments}
                disabled={loading || savingAssignments}
              >
                {savingAssignments ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>Save Category Assignments</>
                )}
              </button>
            </div>          </>
        )}
    </div>
  );
};

export default CategoryAssignment;